import React, { useState, useEffect } from 'react';
import { useFeatureService } from '../admin/services/useFeatureService';
import type { Feature } from '../admin/services/FeatureService';
import { useDynamicEntitiesContext } from '../core/DynamicEntitiesProvider';
import { useDynamicDefinition } from '../core/hooks/useDynamicDefinition';
import { DynamicEntities, type DynamicEntitiesRef } from './DynamicEntities';
import { DynamicEntityMultiRecord } from './DynamicEntityMultiRecord';
import { Button } from '../../Button/Button';
import { LayoutContent } from '../../LayoutContent';

export interface DynamicFeatureOrchestratorProps {
    featureCode: string;
    recordId?: string;
    className?: string;
}

export const EntityContainer = React.forwardRef<DynamicEntitiesRef, {
    featureCode: string,
    entityCode: string,
    recordId?: string,
    hideTitle?: boolean,
    hideSubmitButton?: boolean,
    /** Se reenvía a `DynamicEntities`: avisa si la entidad tiene campos que pintar. */
    onRenderableChange?: (renderable: boolean) => void
}>(({ featureCode, entityCode, recordId, hideTitle, hideSubmitButton, onRenderableChange }, ref) => {
    const { definition, loading } = useDynamicDefinition(featureCode, entityCode);

    if (loading) return <div>Cargando...</div>;
    if (!definition) return null;

    if (!definition.multiRecord) {
        return (
            <div className="w-full">
                <DynamicEntities
                    ref={ref}
                    featureCode={featureCode}
                    entityCode={entityCode}
                    recordId={recordId}
                    hideTitle={hideTitle}
                    hideSubmitButton={hideSubmitButton}
                    onRenderableChange={onRenderableChange}
                />
            </div>
        );
    }

    // MODO MULTI-REGISTRO
    if (!recordId) {
        return <div className="text-gray-500 italic p-4 text-sm">Guarde el registro principal primero para habilitar la adición de múltiples registros en {definition.description || entityCode}.</div>;
    }

    return (
        <DynamicEntityMultiRecord
            ref={ref}
            featureCode={featureCode}
            entityCode={entityCode}
            recordId={recordId}
            definition={definition}
        />
    );
});

export const DynamicFeatureOrchestrator: React.FC<DynamicFeatureOrchestratorProps> = ({
    featureCode,
    recordId,
    className
}) => {
    const { onError } = useDynamicEntitiesContext();
    const service = useFeatureService();
    const [feature, setFeature] = useState<Feature | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<string>('');
    const [modalOpen, setModalOpen] = useState<string | null>(null);

    useEffect(() => {
        service.getByCode(featureCode)
            .then(f => {
                setFeature(f);
                if (f.featureEntities && f.featureEntities.length > 0) {
                    const firstTab = f.featureEntities.find(fe => fe.renderMode === 'tab' || fe.renderMode === 'inline');
                    if (firstTab && firstTab.dynamicEntity) {
                        setActiveTab(firstTab.dynamicEntity.code);
                    }
                }
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                onError?.(err as Error);
                setLoading(false);
            });
    }, [featureCode, service, onError]);

    if (loading) return <div className="p-6 text-gray-500">Cargando Módulo...</div>;
    if (!feature) return <div className="p-6 text-red-500">Módulo no encontrado</div>;

    const tabs = feature.featureEntities?.filter(fe => fe.renderMode === 'tab') || [];
    const modals = feature.featureEntities?.filter(fe => fe.renderMode === 'modal') || [];
    const inlines = feature.featureEntities?.filter(fe => fe.renderMode === 'inline') || [];

    const endContent = modals.length > 0 ? (
        <div className="flex gap-2">
            {modals.map(fe => (
                <Button
                    key={fe.id}
                    onClick={() => setModalOpen(fe.dynamicEntity?.code || null)}
                >
                    {fe.dynamicEntity?.description || fe.dynamicEntity?.code}
                </Button>
            ))}
        </div>
    ) : undefined;

    return (
        <LayoutContent
            title={feature.code}
            className={className}
            toolbarEndContent={endContent}
        >
            <div className="flex flex-col gap-6">
                {tabs.length > 0 && (
                    <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-bg-primary overflow-x-auto">
                        <nav className="-mb-px flex space-x-0" aria-label="Tabs">
                            {tabs.map(fe => (
                                <button
                                    key={fe.id}
                                    onClick={() => setActiveTab(fe.dynamicEntity?.code || '')}
                                    className={`
                                        whitespace-nowrap px-5 py-3 border-b-2 font-medium text-sm transition-colors focus:outline-none
                                        ${activeTab === fe.dynamicEntity?.code
                                            ? 'border-primary-600 text-primary-700 dark:text-primary-500'
                                            : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}
                                    `}
                                >
                                    {fe.dynamicEntity?.description || fe.dynamicEntity?.code}
                                </button>
                            ))}
                        </nav>
                    </div>
                )}

                <div className="w-full">
                    {tabs.map(fe => (
                        <div key={fe.id} className={activeTab === fe.dynamicEntity?.code ? 'block animate-in fade-in duration-200' : 'hidden'}>
                            {fe.dynamicEntity && (
                                <EntityContainer
                                    featureCode={feature.code}
                                    entityCode={fe.dynamicEntity.code}
                                    recordId={recordId}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {inlines.length > 0 && (
                    <div className="flex flex-col gap-6 mt-4">
                        {inlines.map(fe => (
                            <div key={fe.id} className="w-full">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 px-1">
                                    {fe.dynamicEntity?.description || fe.dynamicEntity?.code}
                                </h3>
                                {fe.dynamicEntity && (
                                    <EntityContainer
                                        featureCode={feature.code}
                                        entityCode={fe.dynamicEntity.code}
                                        recordId={recordId}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {modalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                            <div className="flex justify-between items-center p-5 border-b border-gray-100">
                                <h3 className="text-xl font-bold text-gray-900">
                                    {modals.find(m => m.dynamicEntity?.code === modalOpen)?.dynamicEntity?.description}
                                </h3>
                                <button
                                    onClick={() => setModalOpen(null)}
                                    className="p-1 hover:bg-gray-100 rounded-md transition-colors text-gray-500 hover:text-gray-700"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto">
                                <EntityContainer
                                    featureCode={feature.code}
                                    entityCode={modalOpen}
                                    recordId={recordId}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </LayoutContent>
    );
};
